import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import { Container, Row, Col } from 'reactstrap';

import * as AuthorizationStore from '../../store/Authorization';
import * as PartnerCheckoutStore from '../../store/PartnerCheckout';
import { useHistory } from 'react-router-dom';

import { ImpactPartner } from '../../models/ImpactPartner';
import { CheckoutTypes } from '../../models/CheckoutTypes';
import Loader from '../../components/shared/Loader';
import OrderSummary from './OrderSummary';
import { ShoppingCartItem } from '../../models/ShoppingCart';
import ReviewPayment from './ReviewPayment';
import OrderConfirmation from './OrderConfirmation';
import ProductsPropertiesChangedInfoModal from '../../components/shared/ProductsPropertiesChangedInfoModal';
import { PartnerMessage } from '../../models/PartnerMessage';
import { hasRequiredErrors } from './CheckoutUtils';
import { ImpactPartnerResultDto } from '../../models/OrderResponseDto';
import { CheckoutCommonFields } from '../../models/ReceivedOrder';

type PartnerCheckoutProps = {
    cartId: string;
    cartName: string;
    partnerCheckoutState: PartnerCheckoutStore.IState;
    partnerCheckoutActions: typeof PartnerCheckoutStore.actionCreators;
    partner: ImpactPartner;
    currentImpactPartner: ImpactPartner;
    partnersMessages: PartnerMessage[];
    cartItems: ShoppingCartItem[];
    nextPartner?: ImpactPartner;
    sendingOrderResponse: boolean;
    onNext: () => void;
    onContinueShopping: () => void;
    onSendOrderResponse: () => void;
}

const PartnerCheckout: React.FC<PartnerCheckoutProps> = ({
    cartId,
    cartName,
    partner,
    partnerCheckoutState,
    partnerCheckoutActions,
    currentImpactPartner,
    partnersMessages,
    cartItems,
    nextPartner,
    sendingOrderResponse,
    onNext,
    onContinueShopping,
    onSendOrderResponse
}) => {

    const ref = useRef(null);

    const history = useHistory();

    var loggedInPartner = AuthorizationStore.getImpactPartnerFromLocalStorage();

    const [defaultCurrency] = useState({ code: loggedInPartner ? (loggedInPartner.IsCanadian && partner.IsCanadian ? "CAD" : "USD") : "" });

    const [partnerMessage, setPartnerMessage] = useState<PartnerMessage>(null);

    const [openInfoChangedDialog, setOpenInfoChangedDialog] = useState(false);
    const [inProgress, setInProgress] = useState(false);
    const [retryCount, setRetryCount] = useState(0);

    const reviewOrder = () => {
        const btn = document.getElementById(ref.current.props.id);
        btn.click();
    }

    const orderProductsCheck = (orderInfo) => {
        setInProgress(true);
        setRetryCount(retryCount + 1);

        partnerCheckoutActions.checkOrderProducts(cartItems[0].CartId, partner);
    }

    const orderSubmit = (orderInfo) => {

        orderInfo.currencyCode = defaultCurrency.code;
        orderInfo.items = cartItems.slice();

        partnerCheckoutState.checkoutCommonFields.map(field => {
            orderInfo[field.FieldName] = field.Value;
        });

        let order = Object.assign({}, orderInfo);

        order.items = prepareOrderItems(order);

        //console.log('order submitted.');
        partnerCheckoutActions.orderSubmit(cartItems[0].CartId, order, partner);

        partnerCheckoutState.cartProductsVerified = false;

        partnerCheckoutState.cartProductsChanged = false;
    }

    const prepareOrderItems = (order) => {
        return order.items.map((item) => {
            var orderItem = {
                productId: item.PartnerPartNumber,
                productNumber: item.ProductNumber,
                productName: item.Description,
                quantity: item.Amount,
                price: item.PricePerUnit,
                orderCutoffTime: item.OrderCutoffTime,
                orderProcessingTime: item.OrderProcessingTime,
                approvedBy: item.ApprovedBy,
            };

            return orderItem;
        });
    }

    const backToCart = () => {
        partnerCheckoutActions.resetState();
        history.push("/cart");
    }

    useEffect(() => {
        if (currentImpactPartner && partner && currentImpactPartner.Id === partner.Id) {
            if (!partnerCheckoutState.isFetchingCheckoutCommonFields && !partnerCheckoutState.checkoutCommonFieldsLoaded) {
                console.log("PartnerCheckout load common fields");
                partnerCheckoutActions.loadCheckoutCommonFields(cartId, partner.Id);
            }

            if (partnersMessages && partnersMessages.length > 0) {
                var partnerMessage = partnersMessages.find(x => x.PartnerName === currentImpactPartner.FormatedName)
                setPartnerMessage(partnerMessage);
            }
        }

    }, [currentImpactPartner, partner])

    useEffect(() => {
        if (partnerCheckoutState.cartProductsVerified) {

            if (partnerCheckoutState.cartProductsChanged) {
                setOpenInfoChangedDialog(true);
            }
            else {
                orderSubmit(partnerCheckoutState.info);
            }
        }

    }, [partnerCheckoutState.cartProductsVerified])

    useEffect(() => {
        let btn = document.getElementById("btnReviewOrderSummary");
        let btnReviewOrder = document.getElementById("btnReviewOrder");

        if (btn) {
            (btn as any).disabled = partnerCheckoutState.errorLoadedCheckoutCommonFields;
        }

        if (btnReviewOrder) {
            (btnReviewOrder as any).disabled = partnerCheckoutState.errorLoadedCheckoutCommonFields;
        }

    }, [partnerCheckoutState.errorLoadedCheckoutCommonFields])

    useEffect(() => {
        if (partnerCheckoutState.currentType === CheckoutTypes.ORDERSUBMITTED) {

            const errorOnSubmitOrder = !partnerCheckoutState.customerOrderNumber
            && partnerCheckoutState.hasErrorOnSubmit;

            const partnerResults: ImpactPartnerResultDto[] = partnerCheckoutState.partnerResults.slice();
            const index = partnerResults.findIndex(r => r.partnerId === partner.Id);
            const partnerResult: ImpactPartnerResultDto = {
                partnerId: partner.Id,
                partnerName: partner.Name,
                orderResponseStatusCode: partnerCheckoutState.orderSubmitStatusCode
            };

            partnerResult.orderResponseStatusCode = partnerCheckoutState.orderSubmitStatusCode;
            partnerResult.orderResponse = errorOnSubmitOrder
                ? partnerCheckoutState.orderSubmitErrorMessage
                : `${partnerCheckoutState.customerOrderNumber}`;
            partnerResult.poInfoPoNumber = partnerCheckoutState.info[CheckoutCommonFields.PoInfoPoNumber] ?? "";

            if (index === -1) {
                partnerResults.push(partnerResult);
                partnerCheckoutState.partnerResults = partnerResults;
            }
        }
    }, [partnerCheckoutState.currentType])

    const errorLoadedCommonFieldsMessage = () => {
        let message = "There was an issue processing your order. Please try again later.";
        if (!partnerCheckoutState.errorLoadedCheckoutCommonFields &&
            partnerCheckoutState.checkoutCommonFields &&
            partnerCheckoutState.checkoutCommonFields.length === 0) {
            message = "No common fields provided.";
        }

        return <div dangerouslySetInnerHTML={{ __html: message }}></div>
    }

    return (<Container fluid className="h-100">

        {
            !partnerCheckoutState.checkoutCommonFields && !partnerCheckoutState.errorLoadedCheckoutCommonFields &&
            <div className="mt-5 w-100 loader-x3 position-absolute"><Loader /></div>
        }

        <Row className="flex-column-reverse flex-lg-row h-100">
            <Col className="left-side-col py-3 py-lg-4">
                {partnerCheckoutState.currentType !== CheckoutTypes.ORDERSUBMITTED && <h1 className="cart-name">{cartName}</h1>}
                {partnerCheckoutState.currentType === CheckoutTypes.ORDERSUBMITTED && <h1 className="cart-name">Order Confirmation</h1>}
                <h2 className="partner-name mt-4">{partner.Name}</h2>

                {
                    partnerCheckoutState.errorLoadedCheckoutCommonFields
                    || (partnerCheckoutState.checkoutCommonFields &&
                        partnerCheckoutState.checkoutCommonFields.length === 0) &&
                    <>
                        <div className="row mb-4">
                            <div className="col">
                                <div className="section-title order-error-message">
                                    {errorLoadedCommonFieldsMessage()}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex justify-content-start mt-3">
                            {onNext && nextPartner &&
                                <button className="btn btn-primary text-uppercase mr-2" onClick={onNext}>Proceed To {nextPartner.Name}</button>
                            }
                            {
                                (!onNext || !nextPartner) && partnerCheckoutState.partnerResults.length > 0 &&
                                <button className="btn btn-primary text-uppercase d-flex justify-content-start ml-2 mr-2"
                                        onClick={onSendOrderResponse}>
                                    <span>Send Order Response Summary</span>
                                    {
                                        sendingOrderResponse &&
                                        <span className="ml-2"><Loader /></span>
                                    }
                                </button>
                            }
                        </div>
                    </>
                }

                {
                    partnerCheckoutState.checkoutCommonFields
                    && partnerCheckoutState.checkoutCommonFields.length > 0
                    && hasRequiredErrors(partnerCheckoutState.checkoutCommonFields) &&
                    <>
                        <div className="d-flex justify-content-start mt-3 mb-3">
                            {onNext && nextPartner &&
                                <button className="btn btn-primary text-uppercase mr-2" onClick={onNext}>Proceed To {nextPartner.Name}</button>
                            }
                            {
                                (!onNext || !nextPartner) && partnerCheckoutState.partnerResults.length > 0 &&
                                <button className="btn btn-primary text-uppercase d-flex justify-content-start ml-2 mr-2"
                                    onClick={onSendOrderResponse}>
                                    <span>Send Order Response Summary</span>
                                    {
                                        sendingOrderResponse &&
                                        <span className="ml-2"><Loader /></span>
                                    }
                                </button>
                            }
                        </div>
                    </>
                }

                {partnerCheckoutState.checkoutCommonFields && partnerCheckoutState.checkoutCommonFields.length > 0 &&
                    <React.Fragment>
                        {
                            partnerCheckoutState.currentType === CheckoutTypes.REVIEW &&
                            <ReviewPayment
                                model={partnerCheckoutState.info}
                                checkoutCommonFields={partnerCheckoutState.checkoutCommonFields || []}
                                partnerMessage={partnerMessage}
                                customerOrderNumber={partnerCheckoutState.customerOrderNumber}
                                isSubmittingOrder={partnerCheckoutState.isSubmittingOrder}
                                orderSubmit={orderProductsCheck}
                                onBackStepClick={() => { }}
                                retrySubmit={retryCount === 1}
                            />
                        }
                        {
                            partnerCheckoutState.currentType === CheckoutTypes.ORDERSUBMITTED &&
                            <React.Fragment>
                                <OrderConfirmation
                                    partnerCheckoutState={partnerCheckoutState}
                                />
                                <div className="d-flex justify-content-start mt-3">
                                    {onNext && nextPartner &&
                                        <button className="btn btn-primary text-uppercase mr-2" onClick={onNext}>Proceed To {nextPartner.Name}</button>
                                    }
                                    <button className="btn btn-white text-uppercase" onClick={onContinueShopping}>Continue Shopping</button>
                                    {
                                        (!onNext || !nextPartner) && partnerCheckoutState.partnerResults.length > 0 &&
                                        <button className="btn btn-primary text-uppercase d-flex justify-content-start ml-2 mr-2"
                                            onClick={onSendOrderResponse}>
                                            <span>Send Order Response Summary</span>
                                            {
                                                sendingOrderResponse &&
                                                <span className="ml-2"><Loader /></span>
                                            }
                                        </button>
                                    }
                                </div>
                            </React.Fragment>
                        }
                    </React.Fragment>
                }
            </Col>
            <Col className="right-side-col mt-3 mt-lg-0">
                {cartItems &&
                    <OrderSummary
                        paymentModel={partnerCheckoutState.info}
                        items={cartItems}
                        checkoutCommonFields={partnerCheckoutState.checkoutCommonFields || []}
                        step={partnerCheckoutState.currentType}
                        reviewOrder={reviewOrder}
                        isCheckoutFetching={partnerCheckoutState.isFetchingCheckoutCommonFields}
                        completeOrder={orderProductsCheck}
                        inProgress={inProgress || partnerCheckoutState.isSubmittingOrder}
                        backToCart={backToCart}
                        retrySubmit={retryCount === 1}
                    >
                    </OrderSummary>}
            </Col>
        </Row>

        <ProductsPropertiesChangedInfoModal open={openInfoChangedDialog}
            onClose={() => {
                setOpenInfoChangedDialog(false);

                partnerCheckoutState.currentType = CheckoutTypes.ORDERSUBMITTED;
                //document.location.assign('/cart');
                //orderSubmit(partnerCheckoutState.info);
            }}
        />
    </Container>);
}

export default PartnerCheckout;